import React, { useState, useEffect, useCallback, Component } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import WorkoutStepEditor from './WorkoutStepEditor';
import './WorkoutBuilder.css';

// Error boundary to catch rendering errors
class WorkoutBuilderErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('WorkoutBuilder error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="workout-builder-overlay">
          <div className="workout-builder" style={{ padding: '2rem', textAlign: 'center' }}>
            <h2 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Something went wrong</h2>
            <p style={{ color: 'var(--text)', marginBottom: '1rem' }}>
              {this.state.error?.message || 'Failed to load workout builder'}
            </p>
            <button
              onClick={this.props.onClose}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--primary)',
                border: 'none',
                borderRadius: '8px',
                color: '#0D0D0D',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Helper to extract pace string from either string or object format
const getPaceString = (pace) => {
  if (!pace) return '-';
  if (typeof pace === 'string') return pace;
  if (typeof pace === 'object' && pace.pace) return pace.pace;
  return '-';
};

// Helper to get repetition pace per km (not the 400m time)
const getRepPace = (paceData) => {
  if (!paceData) return '-';
  if (typeof paceData === 'object') {
    // Use pace_km for per-km pace, fallback to pace if not available
    if (paceData.pace_km && paceData.pace_km !== '—') return paceData.pace_km;
    if (paceData.pace && paceData.pace !== '—') return paceData.pace;
    return '-';
  }
  return '-';
};

const WorkoutBuilderInner = ({ onClose, garminUserId, onWorkoutCreated, initialDate }) => {
  const auth = useAuth();
  const authFetch = auth?.authFetch;

  // Debug log on mount
  useEffect(() => {
    console.log('WorkoutBuilder mounted', { garminUserId, hasAuthFetch: !!authFetch, initialDate });
  }, [garminUserId, authFetch, initialDate]);
  const [workoutName, setWorkoutName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState([]);
  const [scheduleDate, setScheduleDate] = useState(initialDate || '');
  const [trainingPaces, setTrainingPaces] = useState(null);
  const [vdot, setVdot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isFetchingPaces, setIsFetchingPaces] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Track saved workout state
  const [savedWorkoutId, setSavedWorkoutId] = useState(null);
  const [isGarminSynced, setIsGarminSynced] = useState(false);
  const [garminWorkoutId, setGarminWorkoutId] = useState(null);

  // Fetch user's training paces on mount
  useEffect(() => {
    const fetchPaces = async () => {
      try {
        if (!authFetch) {
          console.warn('authFetch not available');
          setIsFetchingPaces(false);
          return;
        }
        const res = await authFetch('/api/garmin/workouts/paces');
        if (res && res.ok) {
          const data = await res.json();
          setTrainingPaces(data.paces || {});
          setVdot(data.vdot);
        } else {
          console.log('No training paces available (no active plan)');
        }
      } catch (err) {
        console.error('Failed to fetch paces:', err);
      } finally {
        setIsFetchingPaces(false);
      }
    };
    fetchPaces();
  }, [authFetch]);

  // Convert duration value to API format (seconds for time, meters for distance)
  const convertDurationValue = (step) => {
    let value = step.duration_value || 0;

    if (step.duration_type === 'time') {
      // duration_value is already in seconds from the hh:mm:ss input
      return value;
    }

    if (step.duration_type === 'distance') {
      // Convert to meters
      if (step.duration_unit === 'miles') {
        return value * 1609.344; // miles to meters
      }
      return value * 1000; // km to meters
    }

    return value;
  };

  // Flatten steps (expand repeats) and convert to API format
  const convertStepsToApiFormat = useCallback((items) => {
    const apiSteps = [];

    const processStep = (step) => {
      apiSteps.push({
        step_type: 'active',
        duration_type: step.duration_type,
        duration_value: convertDurationValue(step),
        pace_zone: step.pace_zone || null,
        description: step.notes || null
      });
    };

    items.forEach(item => {
      if (item.type === 'repeat') {
        // Expand repeat blocks
        for (let i = 0; i < item.repeat_count; i++) {
          item.steps.forEach(step => processStep(step));
        }
      } else {
        processStep(item);
      }
    });

    return apiSteps;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!workoutName.trim()) {
      setError('Please enter a workout name');
      return;
    }

    if (steps.length === 0) {
      setError('Please add at least one step to your workout');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiSteps = convertStepsToApiFormat(steps);

      // Calculate estimated distance and duration from steps
      let totalDuration = 0;
      let totalDistance = 0;
      apiSteps.forEach(step => {
        if (step.duration_type === 'time' && step.duration_value) {
          totalDuration += step.duration_value;
        } else if (step.duration_type === 'distance' && step.duration_value) {
          totalDistance += step.duration_value / 1000; // meters to km
        }
      });

      // Save workout to database (local storage)
      const response = await authFetch('/api/user/custom-workouts', {
        method: 'POST',
        body: JSON.stringify({
          workout_name: workoutName,
          workout_type: 'Custom',
          description: description || null,
          scheduled_date: scheduleDate || null,
          distance_km: totalDistance > 0 ? totalDistance : null,
          duration_minutes: totalDuration > 0 ? Math.round(totalDuration / 60) : null,
          steps: apiSteps
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSavedWorkoutId(data.workout.id);
        setSuccess('Workout saved! You can now sync it to Garmin.');

        // Notify parent component
        if (onWorkoutCreated) {
          onWorkoutCreated({
            id: data.workout.id,
            workoutName: workoutName,
            description: description,
            scheduled_date: scheduleDate,
            distance_km: totalDistance > 0 ? totalDistance : null,
            duration_minutes: totalDuration > 0 ? Math.round(totalDuration / 60) : null,
            estimatedDistanceInMeters: totalDistance * 1000,
            estimatedDurationInSecs: totalDuration
          });
        }
      } else {
        setError(data.detail || 'Failed to save workout');
      }
    } catch (err) {
      setError(err.message || 'Failed to save workout');
    } finally {
      setIsLoading(false);
    }
  };

  // Sync saved workout to Garmin
  const handleSyncToGarmin = async () => {
    if (!savedWorkoutId) {
      setError('Please save the workout first');
      return;
    }

    if (!garminUserId) {
      setError('Garmin account not connected. Please connect your Garmin first.');
      return;
    }

    setIsSyncing(true);
    setError(null);

    try {
      const response = await authFetch(`/api/garmin/workouts/sync-custom?garmin_user_id=${garminUserId}`, {
        method: 'POST',
        body: JSON.stringify({
          custom_workout_id: savedWorkoutId,
          schedule_date: scheduleDate || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsGarminSynced(true);
        setGarminWorkoutId(data.garmin_workout_id);

        if (data.already_synced) {
          setSuccess('This workout was already synced to Garmin.');
        } else {
          setSuccess(
            data.scheduled
              ? `Synced to Garmin and scheduled for ${data.schedule_date}!`
              : 'Synced to Garmin successfully!'
          );
        }
      } else {
        setError(data.detail || 'Failed to sync to Garmin');
      }
    } catch (err) {
      setError(err.message || 'Failed to sync to Garmin');
    } finally {
      setIsSyncing(false);
    }
  };

  // Reset form to create another workout
  const handleCreateAnother = () => {
    setWorkoutName('');
    setDescription('');
    setSteps([]);
    setScheduleDate(initialDate || '');
    setSavedWorkoutId(null);
    setIsGarminSynced(false);
    setGarminWorkoutId(null);
    setSuccess(null);
    setError(null);
  };

  return (
    <div className="workout-builder-overlay">
      <div className="workout-builder">
        <div className="workout-builder-header">
          <h2>Create Workout</h2>
          {initialDate && (
            <span className="workout-builder-date">
              {new Date(initialDate + 'T00:00:00').toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
          )}
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {isFetchingPaces ? (
          <div className="loading-paces">Loading your training paces...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Pace Zones Display */}
            {trainingPaces && (
              <div className="pace-zones-display">
                <h4>Your Training Paces {vdot && <span className="vdot-badge">VDOT {vdot}</span>}</h4>
                <div className="pace-zones-grid">
                  <div className="pace-zone easy">
                    <span className="zone-label">Easy</span>
                    <span className="zone-pace">{getPaceString(trainingPaces.easy)}</span>
                  </div>
                  <div className="pace-zone marathon">
                    <span className="zone-label">Marathon</span>
                    <span className="zone-pace">{getPaceString(trainingPaces.marathon)}</span>
                  </div>
                  <div className="pace-zone threshold">
                    <span className="zone-label">Threshold</span>
                    <span className="zone-pace">{getPaceString(trainingPaces.threshold)}</span>
                  </div>
                  <div className="pace-zone interval">
                    <span className="zone-label">Interval</span>
                    <span className="zone-pace">{getPaceString(trainingPaces.interval)}</span>
                  </div>
                  <div className="pace-zone repetition">
                    <span className="zone-label">Repetition</span>
                    <span className="zone-pace">{getRepPace(trainingPaces.repetition)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Workout Details */}
            <div className="form-group">
              <label htmlFor="workoutName">Workout Name *</label>
              <input
                type="text"
                id="workoutName"
                value={workoutName}
                onChange={(e) => setWorkoutName(e.target.value)}
                placeholder="e.g., Tuesday Tempo Run"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description (optional)</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Notes about this workout..."
                rows="2"
              />
            </div>

            {/* Steps Editor */}
            <div className="steps-section">
              <h4>Workout Steps</h4>
              <WorkoutStepEditor
                steps={steps}
                onChange={setSteps}
                trainingPaces={trainingPaces}
              />
            </div>

            {/* Schedule Date */}
            <div className="form-group schedule-group">
              <label htmlFor="scheduleDate">Schedule for (optional)</label>
              <input
                type="date"
                id="scheduleDate"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <span className="schedule-hint">Leave empty to save without scheduling</span>
            </div>

            {/* Status Messages */}
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Action Buttons */}
            <div className="form-actions">
              {!savedWorkoutId ? (
                // Not yet saved - show Save button
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={onClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                          <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
                        </svg>
                        Save Workout
                      </>
                    )}
                  </button>
                </>
              ) : (
                // Saved - show Garmin sync options
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCreateAnother}
                  >
                    Create Another
                  </button>
                  {!isGarminSynced ? (
                    <button
                      type="button"
                      className="btn-primary btn-garmin"
                      onClick={handleSyncToGarmin}
                      disabled={isSyncing || !garminUserId}
                    >
                      {isSyncing ? (
                        <>
                          <span className="spinner"></span>
                          Syncing...
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                            <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                          </svg>
                          Sync to Garmin
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-success"
                      disabled
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                      Synced to Garmin
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={onClose}
                  >
                    Done
                  </button>
                </>
              )}
            </div>

            {!savedWorkoutId && !garminUserId && (
              <p className="garmin-required-hint">
                You can save workouts without Garmin, but connect to sync them to your watch
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

// Wrapper component with error boundary
const WorkoutBuilder = (props) => {
  return (
    <WorkoutBuilderErrorBoundary onClose={props.onClose}>
      <WorkoutBuilderInner {...props} />
    </WorkoutBuilderErrorBoundary>
  );
};

export default WorkoutBuilder;
